import type { AudioEventId } from "../audio/ports/types";
import type { HapticPatternName } from "../haptics/HapticPatterns";
import { chromeBandProperties } from "./chromeContentArea";
import { SHELL_FONT_FAMILY } from "@slot/presentation";
import type { ViewportStore } from "@slot/presentation";

/** What a DOM control asks for on pointer-down; the chrome root plays it through the services. */
export interface ChromePressDetail {
  audio?: AudioEventId;
  haptic?: HapticPatternName;
}

export const CHROME_PRESS_EVENT = "slot-press";

/** A DOM control's press: dispatched on pointer-down, bubbles out of any shadow root to the chrome root. */
export function dispatchChromePress(target: EventTarget, detail: ChromePressDetail): void {
  target.dispatchEvent(new CustomEvent<ChromePressDetail>(CHROME_PRESS_EVENT, { detail, bubbles: true, composed: true }));
}

export interface ChromeRoot {
  /** The design-space box the DOM chrome panels are appended to. */
  element: HTMLElement;
  dispose(): void;
}

/** The DOM chrome's theme: the custom properties the elements' stylesheets read. A title overrides any subset. */
export interface ChromeTheme {
  modalDim: string;
  modalWindow: string;
  modalText: string;
  modalPrimary: string;
  modalSecondary: string;
  buttonText: string;
  /** A modal window's max width in design units; the mobile landscape window is wider. */
  modalMaxWidth: number;
  modalMaxWidthMobileLandscape: number;
  /**
   * The footer shade (decision 44): on the mobile shapes the bar backs its bottom row — balance, bet, win — with a
   * gradient from this colour at the screen's bottom edge to transparent, so the numbers read over any background.
   * Any CSS colour; `transparent` turns it off. Desktop has its textured bar and draws none.
   */
  footerShade: string;
  /** The shade's height as a multiple of the bottom band (1 = the numbers' row, more bleeds into the scene above it). */
  footerShadeHeight: number;
}

export const SHELL_CHROME_THEME: ChromeTheme = {
  modalDim: `rgba(0, 0, 0, ${(189 / 255).toFixed(3)})`,
  modalWindow: "#ffffff",
  modalText: "#000000",
  modalPrimary: "#00aa5a",
  modalSecondary: "#ff8000",
  buttonText: "#ffffff",
  modalMaxWidth: 868.5,
  modalMaxWidthMobileLandscape: 1200,
  footerShade: "rgba(0, 0, 0, 0.6)",
  footerShadeHeight: 1.5,
};

/**
 * The DOM chrome root: a 1080-wide-or-wider, 1920-tall box in DESIGN UNITS, centred and scaled with
 * the same fit-height factor as the canvas, so a DOM panel lays out in the art's units. It carries
 * the shape (`data-shape`), the sizes (`--slot-design-width`, `--slot-css-width` / `--slot-css-height`, unitless)
 * and the theme as custom properties; per-shape layout is CSS. Pointer
 * events pass through to the canvas except where a panel opts in. Presses from DOM controls arrive
 * as `slot-press` events and play the click cue and haptic through the injected services.
 */
export function createChromeRoot(input: {
  overlayRoot: HTMLElement;
  viewport: ViewportStore;
  audio: { emit(eventId: AudioEventId): void };
  haptics: { play(pattern: HapticPatternName): void };
  /** The title's overrides over the shell theme. */
  theme?: Partial<ChromeTheme>;
  /** The dev pause: while paused, the chrome's CSS animations and transitions freeze with the game. */
  pause?: { subscribePaused(listener: (paused: boolean) => void): () => void };
}): ChromeRoot {
  const element = document.createElement("div");
  element.className = "slot-chrome";
  const style = element.style;
  style.position = "absolute";
  style.left = "50%";
  style.top = "50%";
  style.pointerEvents = "none";
  style.fontFamily = SHELL_FONT_FAMILY;
  const theme: ChromeTheme = { ...SHELL_CHROME_THEME, ...input.theme };
  style.setProperty("--slot-modal-dim", theme.modalDim);
  style.setProperty("--slot-modal-window", theme.modalWindow);
  style.setProperty("--slot-modal-text", theme.modalText);
  style.setProperty("--slot-modal-primary", theme.modalPrimary);
  style.setProperty("--slot-modal-secondary", theme.modalSecondary);
  style.setProperty("--slot-button-text", theme.buttonText);
  style.setProperty("--slot-footer-shade", theme.footerShade);
  style.setProperty("--slot-footer-shade-height", String(theme.footerShadeHeight));
  // Appended after the dev badge (dev mode), so a page, a modal or a popover paints over the badge and its tap never
  // takes a press meant for a close button under it; the dev menu, appended once the runtime exists, paints above all.
  // Until 2026-10-01 the root was prepended to keep the badge on top (decision 37).
  input.overlayRoot.append(element);

  const unsubscribe = input.viewport.subscribe((s) => {
    style.width = `${s.design.width}px`;
    style.height = `${s.design.height}px`;
    style.transform = `translate(-50%, -50%) scale(${s.scale})`;
    style.setProperty("--slot-design-width", String(s.design.width));
    style.setProperty("--slot-css-width", String(s.css.width));
    style.setProperty("--slot-css-height", String(s.css.height));
    style.setProperty("--slot-modal-max-width", String(s.shape === "mobile_landscape" ? theme.modalMaxWidthMobileLandscape : theme.modalMaxWidth));
    element.dataset.shape = s.shape;
    for (const [name, value] of Object.entries(chromeBandProperties(s))) style.setProperty(name, value);
  });

  // The dev pause: every running CSS animation / transition under the chrome (shadow trees included)
  // pauses, and the paused ones resume together. A finished one is left alone: filled forwards it stays in
  // getAnimations(), and play() would rewind it to its start (review 2026-09-27 #31). Motion that starts
  // while paused runs; the pause is a dev aid.
  const unsubscribePause =
    input.pause?.subscribePaused((paused) => {
      for (const animation of document.getAnimations()) {
        const target = (animation.effect as KeyframeEffect | null)?.target;
        if (!target || !isUnder(element, target)) continue;
        if (paused && animation.playState === "running") animation.pause();
        else if (!paused && animation.playState === "paused") animation.play();
      }
    }) ?? null;

  const onPress = (event: Event): void => {
    const detail = (event as CustomEvent<ChromePressDetail>).detail;
    if (detail.audio) input.audio.emit(detail.audio);
    if (detail.haptic) input.haptics.play(detail.haptic);
  };
  element.addEventListener(CHROME_PRESS_EVENT, onPress);

  return {
    element,
    dispose(): void {
      unsubscribe();
      unsubscribePause?.();
      element.removeEventListener(CHROME_PRESS_EVENT, onPress);
      element.remove();
    },
  };
}

/** Whether `node` sits under `root`, crossing shadow boundaries through their hosts. */
function isUnder(root: Node, node: Node): boolean {
  let current: Node | null = node;
  while (current) {
    if (current === root) return true;
    current = current.parentNode ?? (current as ShadowRoot).host ?? null;
  }
  return false;
}
